import { useEffect, useState } from "react";
import AppHeader from "@/components/AppHeader";
import KnowledgeCenter from "@/components/KnowledgeCenter";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Copy,
  Bitcoin,
  TrendingUp,
  TrendingDown,
  Wallet,
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  X,
  CheckCircle2,
  ShieldAlert,
  MessageCircle,
} from "lucide-react";

const fmtEur = (n) =>
  `€${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ClientDashboard() {
  const { user, btcEur, refreshUser } = useAuth();
  const { t } = useLang();
  const [wallet, setWallet] = useState("");
  const [prices, setPrices] = useState(null);
  const [txs, setTxs] = useState([]);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showUnverified, setShowUnverified] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [w, p, t, s] = await Promise.all([
          api.get("/wallet/deposit"),
          api.get("/crypto/prices"),
          api.get("/transactions"),
          api.get("/settings/public"),
        ]);
        setWallet(w.data.address);
        setPrices(p.data);
        setTxs(t.data);
        setWhatsappNumber(s.data.whatsapp_number || "");
        refreshUser();
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []); // eslint-disable-line

  const btcPrice = prices?.bitcoin?.eur || btcEur || 0;
  const balanceBtc = user?.balance_btc || 0;
  const balanceEur = balanceBtc * btcPrice;

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} to clipboard`);
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12" data-testid="client-dashboard">
        <div className="mb-8 sm:mb-10 flex flex-col gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
              {t("cd.portal")}
            </p>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter break-words">
                <span className="font-light text-white/60">{t("cd.welcome")}</span>{" "}
                {user?.name || user?.email}
              </h1>
              {user?.role !== "admin" && (
                user?.verified ? (
                  <span
                    data-testid="verified-badge"
                    className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#10B981] text-[#10B981] text-[10px] sm:text-xs font-mono uppercase tracking-widest"
                  >
                    <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    {t("cd.verified")}
                  </span>
                ) : (
                  <button
                    data-testid="unverified-badge"
                    onClick={() => setShowUnverified(true)}
                    className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#F23F42] text-[#F23F42] text-[10px] sm:text-xs font-mono uppercase tracking-widest hover:bg-[#F23F42]/10 transition-colors"
                  >
                    <ShieldAlert className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    {t("cd.unverified")}
                  </button>
                )
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-3">
            <button
              data-testid="open-deposit-btn"
              onClick={() => setShowDeposit(true)}
              className="px-4 py-2.5 bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <ArrowDownToLine className="w-4 h-4" /> {t("cd.deposit")}
            </button>
            <button
              data-testid="open-withdraw-btn"
              onClick={() => setShowWithdraw(true)}
              className="px-4 py-2.5 border border-white/20 hover:bg-white/10 flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <ArrowUpFromLine className="w-4 h-4" /> {t("cd.withdraw")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* BALANCE - primary */}
          <div className="md:col-span-2 lg:col-span-2 bg-[#0A0A0A] border border-white/10 p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Wallet className="w-5 h-5 text-[#FFD700]" />
            </div>
            <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-6">
              {t("cd.balance")}
            </p>
            <div className="font-mono text-3xl sm:text-4xl lg:text-5xl font-bold break-words" data-testid="balance-eur">
              {fmtEur(balanceEur)} <span className="text-[#FFD700] text-lg sm:text-xl lg:text-2xl">EUR</span>
            </div>
            <div className="mt-2 font-mono text-sm sm:text-base lg:text-lg text-white/60 break-all" data-testid="balance-btc">
              ≈ {balanceBtc.toFixed(8)} BTC
            </div>
            <div className="mt-8 h-px bg-white/10" />
            <p className="text-xs text-white/40 mt-4 font-mono">
              {t("cd.balanceHelp")}
            </p>
          </div>

          {/* BTC PRICE */}
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bitcoin className="w-4 h-4 text-[#FFD700]" />
              <span className="text-xs font-mono uppercase tracking-widest text-white/50">
                {t("cd.btcLive")}
              </span>
            </div>
            <div className="font-mono text-2xl font-bold" data-testid="btc-price">
              {btcPrice > 0 ? fmtEur(btcPrice) : "—"}
            </div>
            <PriceChange change={prices?.bitcoin?.eur_24h_change} />
          </div>

          {/* ETH PRICE */}
          <div className="bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-white/60" />
              <span className="text-xs font-mono uppercase tracking-widest text-white/50">
                {t("cd.ethLive")}
              </span>
            </div>
            <div className="font-mono text-2xl font-bold">
              {prices?.ethereum?.eur ? fmtEur(prices.ethereum.eur) : "—"}
            </div>
            <PriceChange change={prices?.ethereum?.eur_24h_change} />
          </div>

          {/* DEPOSIT WALLET */}
          <div className="md:col-span-3 lg:col-span-4 bg-[#0A0A0A] border border-white/10 p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6 gap-4">
              <div className="min-w-0">
                <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
                  {t("cd.depositWallet")}
                </p>
                <h2 className="font-display text-xl sm:text-2xl font-black tracking-tighter">
                  {t("cd.depositTitle")}
                </h2>
              </div>
              <div className="hidden sm:block bg-white p-2 shrink-0">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(wallet)}`}
                  alt="QR code"
                  className="w-20 h-20 sm:w-24 sm:h-24"
                  data-testid="wallet-qr"
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <div
                data-testid="deposit-wallet-address"
                className="flex-1 bg-black border border-white/20 px-4 py-3 font-mono text-sm break-all"
              >
                {wallet || "—"}
              </div>
              <button
                onClick={() => copy(wallet, "Wallet address")}
                data-testid="copy-wallet-btn"
                className="px-5 py-3 border border-white/20 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Copy className="w-4 h-4" />
                {t("cd.copy")}
              </button>
            </div>
            <p className="text-xs text-white/40 mt-4 font-mono">
              {t("cd.walletWarning")}
            </p>
          </div>

          {/* MARKET SNAPSHOT */}
          <div className="md:col-span-3 lg:col-span-2 bg-[#0A0A0A] border border-white/10 p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">
              {t("cd.marketSnapshot")}
            </p>
            <div className="space-y-3">
              {[
                { key: "bitcoin", sym: "BTC" },
                { key: "ethereum", sym: "ETH" },
                { key: "tether", sym: "USDT" },
                { key: "solana", sym: "SOL" },
                { key: "ripple", sym: "XRP" },
                { key: "cardano", sym: "ADA" },
              ].map((c) => (
                <div
                  key={c.key}
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                >
                  <span className="font-mono text-sm text-white/70">{c.sym}</span>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-sm">
                      {prices?.[c.key]?.eur ? fmtEur(prices[c.key].eur) : "—"}
                    </span>
                    <PriceChange change={prices?.[c.key]?.eur_24h_change} small />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* TX LOG */}
          <div className="md:col-span-3 lg:col-span-2 bg-[#0A0A0A] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-[#FFD700]" />
              <p className="text-xs font-mono uppercase tracking-widest text-white/50">
                {t("cd.txLog")}
              </p>
            </div>
            {txs.length === 0 ? (
              <p className="text-sm text-white/40 py-8 text-center font-mono">
                {t("cd.noTx")}
              </p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="tx-list">
                {txs.map((t2) => (
                  <div key={t2.id} className="border border-white/10 p-3 flex justify-between items-start">
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-white/50">
                        {t("cd.balanceRow")}
                      </div>
                      <div className="text-xs text-white/60 mt-1">{t2.note}</div>
                      <div className="text-xs text-white/30 mt-1 font-mono">
                        {new Date(t2.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div
                      className={`font-mono text-sm font-bold ${
                        t2.amount_btc >= 0 ? "text-[#10B981]" : "text-[#F23F42]"
                      }`}
                    >
                      {t2.amount_btc >= 0 ? "+" : ""}
                      {t2.amount_btc.toFixed(8)} BTC
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* KNOWLEDGE CENTER — logged-in users only */}
        <KnowledgeCenter />
      </main>

      {showDeposit && (
        <DepositModal
          wallet={wallet}
          onCopy={() => copy(wallet, "Wallet address")}
          onClose={() => setShowDeposit(false)}
        />
      )}
      {showWithdraw && (
        <WithdrawModal
          balanceEur={balanceEur}
          wallet={wallet}
          verified={user?.verified}
          onOpenDeposit={() => {
            setShowWithdraw(false);
            setShowDeposit(true);
          }}
          onClose={() => setShowWithdraw(false)}
        />
      )}
      {showUnverified && (
        <UnverifiedModal
          onDeposit={() => {
            setShowUnverified(false);
            setShowDeposit(true);
          }}
          onClose={() => setShowUnverified(false)}
        />
      )}

      {whatsappNumber && (
        <a
          data-testid="whatsapp-fab"
          href={`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, "")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] shadow-lg flex items-center justify-center transition-colors"
          title="Chat with us on WhatsApp"
          aria-label="WhatsApp support"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </a>
      )}
    </div>
  );
}

function UnverifiedModal({ onDeposit, onClose }) {
  const { t } = useLang();
  return (
    <ModalShell tag={t("unv.tag")} title={t("unv.title")} onClose={onClose}>
      <div className="border border-[#F23F42]/40 bg-[#F23F42]/5 p-5 mb-5">
        <p className="text-sm text-white/80 leading-relaxed">
          {t("unv.body1")}{" "}
          <span className="text-[#FFD700] font-semibold">
            {t("unv.body2")}
          </span>{" "}
          {t("unv.body3")}
        </p>
      </div>
      <button
        onClick={onDeposit}
        data-testid="unverified-deposit-btn"
        className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
      >
        <ArrowDownToLine className="w-4 h-4" />
        {t("unv.cta")}
      </button>
    </ModalShell>
  );
}

function PriceChange({ change, small }) {
  if (change == null) return null;
  const positive = change >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div
      className={`flex items-center gap-1 mt-2 font-mono ${
        small ? "text-xs" : "text-sm"
      } ${positive ? "text-[#10B981]" : "text-[#F23F42]"}`}
    >
      <Icon className="w-3 h-3" />
      {change.toFixed(2)}%
    </div>
  );
}

function ModalShell({ title, tag, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full p-8 relative my-auto">
        <button
          onClick={onClose}
          data-testid="modal-close-btn"
          className="absolute top-4 right-4 p-1 hover:bg-white/10"
        >
          <X className="w-4 h-4" />
        </button>
        <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
          {tag}
        </p>
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

function DepositModal({ wallet, onCopy, onClose }) {
  const { t } = useLang();
  return (
    <ModalShell tag={t("cd.deposit") + " /"} title={t("dep.title")} onClose={onClose}>
      <div className="flex justify-center mb-5">
        <div className="bg-white p-2">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(wallet)}`}
            alt="QR"
            className="w-40 h-40"
          />
        </div>
      </div>
      <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
        {t("dep.address")}
      </label>
      <div
        data-testid="deposit-modal-address"
        className="bg-black border border-white/20 px-3 py-2.5 font-mono text-sm break-all mb-3"
      >
        {wallet || "—"}
      </div>
      <button
        onClick={onCopy}
        data-testid="deposit-modal-copy-btn"
        className="w-full px-4 py-2.5 border border-white/20 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 text-sm"
      >
        <Copy className="w-4 h-4" />
        {t("dep.copyBtn")}
      </button>
      <p className="text-xs text-white/40 mt-4 font-mono text-center">
        {t("cd.walletWarning")}
      </p>
    </ModalShell>
  );
}

function WithdrawModal({ balanceEur, wallet, verified, onOpenDeposit, onClose }) {
  const { t } = useLang();
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const val = parseFloat(amount);
    if (!(val > 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/withdrawals/quote", { amount_eur: val });
      setQuote(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Unverified: show notice + QR/wallet instead of amount input
  if (!verified) {
    return (
      <ModalShell tag="/ Withdraw" title={t("wd.unverifiedTitle")} onClose={onClose}>
        <div className="border border-[#F23F42]/40 bg-[#F23F42]/5 p-5 mb-5">
          <p className="text-sm text-white/85 leading-relaxed">
            {t("wd.unverifiedNotice")}
          </p>
        </div>
        <div className="flex justify-center mb-4">
          <div className="bg-white p-2">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(wallet || "")}`}
              alt="QR"
              className="w-40 h-40"
              data-testid="withdraw-unverified-qr"
            />
          </div>
        </div>
        <label className="text-xs uppercase tracking-widest text-white/60 font-mono block mb-2">
          {t("dep.address")}
        </label>
        <div
          data-testid="withdraw-unverified-address"
          className="bg-black border border-white/20 px-3 py-2.5 font-mono text-sm break-all"
        >
          {wallet || "—"}
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell tag={t("cd.withdraw") + " /"} title={t("wd.title")} onClose={onClose}>
      <p
        data-testid="withdraw-blocked-notice"
        className="-mt-2 mb-4 text-sm text-amber-400 font-mono"
      >
        {t("wd.blockedNotice")}
      </p>
      {!quote ? (
        <form onSubmit={submit} className="space-y-4" data-testid="withdraw-form">
          <div>
            <div className="flex justify-between items-baseline mb-2">
              <label className="text-xs uppercase tracking-widest text-white/60 font-mono">
                {t("wd.amount")}
              </label>
              <span className="text-xs font-mono text-white/40">
                {t("wd.available")} {fmtEur(balanceEur)}
              </span>
            </div>
            <input
              data-testid="withdraw-amount-input"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              required
              className="w-full bg-transparent border border-white/20 focus:border-white outline-none px-3 py-3 font-mono text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="withdraw-submit-btn"
            className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
          >
            <ArrowUpFromLine className="w-4 h-4" />
            {loading ? t("wd.processing") : t("wd.submit")}
          </button>
        </form>
      ) : (
        <div data-testid="withdraw-result" className="space-y-4">
          <div className="border border-[#FFD700]/40 bg-[#FFD700]/5 p-5">
            <p className="text-xs font-mono uppercase tracking-widest text-[#FFD700] mb-2">
              {t("wd.verificationRequired")}
            </p>
            <p className="font-mono text-2xl font-bold mb-1">
              {t("wd.taxLabel")} {quote.tax_percentage}%
            </p>
            <p className="text-sm text-white/70 leading-relaxed">
              {quote.tax_percentage > 0 ? (
                <>
                  {t("wd.mustDeposit1")}{" "}
                  <span
                    data-testid="withdraw-required-deposit"
                    className="text-[#FFD700] font-mono font-bold"
                  >
                    {fmtEur(quote.required_deposit_eur)}
                  </span>{" "}
                  {t("wd.mustDeposit2")}{" "}
                  <span className="font-mono">{fmtEur(quote.requested_amount_eur)}</span>.
                </>
              ) : (
                t("wd.noTax")
              )}
            </p>
          </div>
          {quote.tax_percentage > 0 && (
            <button
              onClick={onOpenDeposit}
              data-testid="withdraw-deposit-now-btn"
              className="w-full bg-[#FFD700] hover:bg-[#F4C430] text-black font-semibold py-3 flex items-center justify-center gap-2 transition-colors"
            >
              <ArrowDownToLine className="w-4 h-4" />
              {t("wd.depositNow")}
            </button>
          )}
          <button
            onClick={() => setQuote(null)}
            data-testid="withdraw-back-btn"
            className="w-full border border-white/20 hover:bg-white/10 py-2.5 text-sm transition-colors"
          >
            {t("wd.back")}
          </button>
        </div>
      )}
    </ModalShell>
  );
}
